import React, { useMemo } from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LOADER_OPTIONS } from '../config/googleMaps';

const MAP_CONTAINER_STYLE = { width: '100%', height: '320px', borderRadius: '1rem' };

const DRIVER_ICON = {
  path: 'M0,0 m-6,-6 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0',
  fillColor: '#059669',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2,
  scale: 1,
};

// Google returns route.encodedPolyline using the standard Google polyline
// algorithm (see backend routeOptimization.js encodePolyline), which the Maps
// JS SDK's geometry library can decode directly.
const decodePolyline = (encoded) => {
  if (!encoded || !window.google?.maps?.geometry?.encoding) return [];
  return window.google.maps.geometry.encoding
    .decodePath(encoded)
    .map((point) => ({ lat: point.lat(), lng: point.lng() }));
};

const DeliveryTrackingMap = ({ driverLocation, destinationCoordinates, storeCoordinates, route }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const path = useMemo(
    () => (isLoaded ? decodePolyline(route?.encodedPolyline) : []),
    [isLoaded, route?.encodedPolyline],
  );

  const center = driverLocation
    ? { lat: driverLocation.lat, lng: driverLocation.lng }
    : destinationCoordinates || storeCoordinates || { lat: 0, lng: 0 };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800">
        Map preview is unavailable — Google Maps API key is not configured.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="rounded-2xl bg-emerald-50 h-80 animate-pulse" aria-hidden="true" />;
  }

  return (
    <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} center={center} zoom={14} options={{ disableDefaultUI: true, zoomControl: true }}>
      {driverLocation && (
        <Marker position={{ lat: driverLocation.lat, lng: driverLocation.lng }} icon={DRIVER_ICON} title="Driver" />
      )}
      {destinationCoordinates && (
        <Marker position={{ lat: destinationCoordinates.lat, lng: destinationCoordinates.lng }} title="Delivery address" />
      )}
      {path.length > 1 && (
        <Polyline path={path} options={{ strokeColor: '#059669', strokeWeight: 4, strokeOpacity: 0.8 }} />
      )}
    </GoogleMap>
  );
};

export default DeliveryTrackingMap;
